import { type ReactNode, useContext, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { InputPercentage } from '@/design_system/InputNumber';
import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Toggle from '@/design_system/Toggle';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconCheck from '@/icons/Check.svg';
import IconCloth from '@/icons/Cloth.svg';
import IconCross from '@/icons/Cross.svg';
import IconWorkshop from '@/icons/Workshop.svg';
import {
  AgreementFromGetById,
  AMOUNT_TO_CONSIDER,
  useDeleteAgreement,
  useUpdateAgreement,
} from '@/models/agreements';
import { useBrands } from '@/models/brand';
import { useEntities } from '@/models/entity';
import { useEntityGroups } from '@/models/entityGroup';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { useCountries } from '@/services/i18n';
import { CURRENCIES } from '@/utils/number';
import { NullablePriceAmount, recomputePrice } from '@/utils/price';

import { PriceCatalogAgreementTables } from './components/PriceAgreementTable';
import { AgreementEditContext } from './AgreementEditContext';
import { AgreementTypeInfo } from './AgreementTypeInfo';

export const AgreementEdit = ({
  agreement,
  onSaveOrCancel,
}: {
  agreement: AgreementFromGetById;
  onSaveOrCancel: () => void;
}) => {
  const [draft, setDraft] = useState<AgreementFromGetById>(structuredClone(agreement));

  const updateDraft = (partialDraft: Partial<AgreementFromGetById>) => {
    setDraft((draft) => ({ ...draft, ...partialDraft }));
  };

  const updatePrice = (priceId: string, partialPrice: Partial<NullablePriceAmount>) => {
    setDraft((draft) => ({
      ...draft,
      prices: draft.prices.map((price) =>
        price.id === priceId
          ? {
              ...price,
              ...recomputePrice(price, partialPrice),
            }
          : price
      ),
    }));
  };

  const { mutateAsync: updateAgreement, isPending: isUpdateAgreementPending } = useUpdateAgreement(
    agreement.id
  );

  const isAfterSales = agreement.type === 'after_sales';

  const {
    data: { entities: afterSalesEntities = [] } = {},
    isLoading: isLoadingAfterSalesEntities,
  } = useEntities(
    {
      limit: 500,
      offset: 0,
      traits: ['aftersales'],
    },
    { enabled: isAfterSales }
  );
  const { data: { entities: storeEntities = [] } = {}, isLoading: isLoadingStoreEntities } =
    useEntities(
      {
        limit: 500,
        offset: 0,
        traits: ['store'],
      },
      { enabled: isAfterSales }
    );
  const { data: { entities = [] } = {}, isLoading: isLoadingEntities } = useEntities(
    {
      limit: 500,
      offset: 0,
    },
    { enabled: !isAfterSales }
  );
  const { data: { entityGroups = [] } = {}, isLoading: isLoadingEntityGroups } = useEntityGroups({
    limit: 500,
    offset: 0,
  });

  const entitiesForSideA = isAfterSales ? afterSalesEntities : entities;
  const entitiesForSideB = isAfterSales ? storeEntities : entities;
  const isLoadingEntitiesForSideA = isAfterSales ? isLoadingAfterSalesEntities : isLoadingEntities;
  const isLoadingEntitiesForSideB = isAfterSales ? isLoadingStoreEntities : isLoadingEntities;

  const handleSave = () => {
    updateAgreement({
      entityIdsA: (isAfterSales ? draft.entitiesA.slice(0, 1) : draft.entitiesA).map(
        (entity) => entity.id
      ),
      entityGroupIdsA: isAfterSales ? [] : draft.entityGroupsA.map((group) => group.id),
      clientA: draft.clientA,
      entityIdsB: draft.entitiesB.map((entity) => entity.id),
      entityGroupIdsB: draft.entityGroupsB.map((group) => group.id),
      clientB: draft.clientB,
      articleWorkshopIds: draft.articleWorkshops.map((workshop) => workshop.id),
      articleBrandIds: draft.articleBrands.map((brand) => brand.id),
      defaultCustomPriceTaxRates: draft.defaultCustomPriceTaxRates,
      currency: draft.currency,
      margin: draft.margin,
      amountToConsider: draft.amountToConsider,
      selfHandledTransitEnabled: draft.selfHandledTransitEnabled,
      supportedCountries: draft.supportedCountries,
      prices: draft.prices.map((price) => ({
        id: price.id,
        actionTypeOrganizationId: price.actionTypeOrganizationId,
        packActionTypeOrganizationId: price.packActionTypeOrganizationId,
        sparePartTypeOrganizationId: price.sparePartTypeOrganizationId,
        feeTypeSlug: price.feeType?.slug,
        currency: price.currency,
        amountBeforeTaxes: price.amountBeforeTaxes,
        amount: price.amount,
        taxes: price.taxes,
        margin: price.margin,
        workshopWarrantyDurationInMonths: price.workshopWarrantyDurationInMonths,
      })),
    });
    onSaveOrCancel();
  };

  return (
    <AgreementEditContext.Provider
      value={{ draft, updateDraft, updatePrice, isUpdateAgreementPending }}
    >
      <main>
        <Stack gap="0.5rem">
          <Stack
            row
            className="settings-section__record-header"
            gap="1rem"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack row gap="0.5rem" alignItems="center">
              <SettingsBreadcrumbParent
                to="/settings_beta/network/agreements"
                label={
                  <Trans id="settings.organization.entities.agreements.detail.back">
                    Agreements
                  </Trans>
                }
              />
              <Title as="h1" size="200" weight="medium">
                <Trans id="settings.organization.entities.agreements.detail.title">Agreement</Trans>
              </Title>
            </Stack>
            <Stack row gap="0.5rem">
              <Button
                iconOnly
                variant="primary"
                size="medium"
                onPress={handleSave}
                isLoading={isUpdateAgreementPending}
                disabled={isUpdateAgreementPending}
              >
                <IconCheck />
              </Button>
              <Button
                iconOnly
                variant="secondary-danger"
                size="medium"
                onPress={onSaveOrCancel}
                disabled={isUpdateAgreementPending}
              >
                <IconCross />
              </Button>
            </Stack>
          </Stack>
          <AgreementTypeInfo type={agreement.type} />
          <Box padding="0">
            <Stack>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  padding: '1rem',
                }}
              >
                <AgreementSideEdit
                  label={
                    <Trans id="settings.organization.entities.agreements.detail.left-side">
                      Between ...
                    </Trans>
                  }
                  entities={draft.entitiesA}
                  entityGroups={draft.entityGroupsA}
                  client={draft.clientA}
                  entityOptions={entitiesForSideA}
                  entityGroupOptions={entityGroups}
                  isLoadingEntities={isLoadingEntitiesForSideA}
                  isLoadingEntityGroups={isLoadingEntityGroups}
                  isDisabled={isUpdateAgreementPending}
                  showEntityGroups={agreement.type !== 'after_sales'}
                  isEntitiesMulti={agreement.type !== 'after_sales'}
                  onChangeEntities={(entitiesA) => updateDraft({ entitiesA })}
                  onChangeEntityGroups={(entityGroupsA) => updateDraft({ entityGroupsA })}
                />
                <AgreementSideEdit
                  label={
                    <Trans id="settings.organization.entities.agreements.detail.right-side">
                      and ...
                    </Trans>
                  }
                  entities={draft.entitiesB}
                  entityGroups={draft.entityGroupsB}
                  client={draft.clientB}
                  entityOptions={entitiesForSideB}
                  entityGroupOptions={entityGroups}
                  isLoadingEntities={isLoadingEntitiesForSideB}
                  isLoadingEntityGroups={isLoadingEntityGroups}
                  isDisabled={isUpdateAgreementPending}
                  disableEntitiesWhenClient={agreement.type !== 'after_sales'}
                  onChangeEntities={(entitiesB) => updateDraft({ entitiesB })}
                  onChangeEntityGroups={(entityGroupsB) => updateDraft({ entityGroupsB })}
                  onChangeClient={
                    agreement.type === 'transit'
                      ? undefined
                      : (clientB) => {
                          if (agreement.type === 'after_sales') {
                            updateDraft({
                              clientB,
                              supportedCountries: clientB ? draft.supportedCountries : [],
                            });
                          } else {
                            updateDraft({ clientB, entitiesB: [], entityGroupsB: [] });
                          }
                        }
                  }
                />
              </div>

              {agreement.type === 'price_catalog' && (
                <>
                  <hr />
                  <AgreementAppliesWhenEdit />
                </>
              )}
            </Stack>
          </Box>

          {agreement.type === 'price_catalog' && <PriceCatalogAgreementEdit />}

          {agreement.type === 'transit' && <TransitAgreementEdit />}

          {agreement.type === 'after_sales' && draft.clientB && <AfterSalesAgreementEdit />}

          <AgreementDelete />
        </Stack>
      </main>
    </AgreementEditContext.Provider>
  );
};

const AgreementSideEdit = ({
  label,
  entities,
  entityGroups,
  client,
  entityOptions,
  entityGroupOptions,
  isLoadingEntities,
  isLoadingEntityGroups,
  isDisabled,
  disableEntitiesWhenClient = true,
  showEntityGroups = true,
  isEntitiesMulti = true,
  onChangeEntities,
  onChangeEntityGroups,
  onChangeClient,
}: {
  label: ReactNode;
  entities: { id: string; name: string }[];
  entityGroups: { id: string; name: string }[];
  client: boolean;
  entityOptions: { id: string; name: string }[];
  entityGroupOptions: { id: string; name: string }[];
  isLoadingEntities: boolean;
  isLoadingEntityGroups: boolean;
  isDisabled: boolean;
  disableEntitiesWhenClient?: boolean;
  showEntityGroups?: boolean;
  isEntitiesMulti?: boolean;
  onChangeEntities: (entities: { id: string; name: string }[]) => void;
  onChangeEntityGroups: (entityGroups: { id: string; name: string }[]) => void;
  onChangeClient?: (client: boolean) => void;
}) => {
  const { t } = useLingui();
  const entitiesDisabled = isDisabled || (disableEntitiesWhenClient && client);

  return (
    <Stack gap="0.75rem">
      <Title color="secondary" weight="medium">
        {label}
      </Title>
      {showEntityGroups && (
        <InputSearchSelect
          isMulti
          size="medium"
          isLoading={isLoadingEntityGroups}
          isDisabled={entitiesDisabled}
          label={t({
            id: 'settings.organization.entities.agreements.detail.edit.entity-groups',
            message: 'Entity groups',
          })}
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.edit.entity-groups.placeholder',
            message: 'Select entity groups',
          })}
          value={entityGroups}
          options={entityGroupOptions}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          onChange={(options) => onChangeEntityGroups([...options])}
          onRemoveValue={(id) => {
            onChangeEntityGroups(entityGroups.filter((group) => group.id !== id));
          }}
        />
      )}
      {isEntitiesMulti ? (
        <InputSearchSelect
          isMulti
          size="medium"
          isLoading={isLoadingEntities}
          isDisabled={entitiesDisabled}
          label={t({
            id: 'settings.organization.entities.agreements.detail.edit.entities',
            message: 'Entities',
          })}
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.edit.entities.placeholder',
            message: 'Select entities',
          })}
          value={entities}
          options={entityOptions}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          onChange={(options) => onChangeEntities([...options])}
          onRemoveValue={(id) => {
            onChangeEntities(entities.filter((entity) => entity.id !== id));
          }}
        />
      ) : (
        <InputSearchSelect
          size="medium"
          isLoading={isLoadingEntities}
          isDisabled={entitiesDisabled}
          label={t({
            id: 'settings.organization.entities.agreements.detail.edit.entity',
            message: 'Entity',
          })}
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.edit.entity.placeholder',
            message: 'Select entity',
          })}
          value={entities[0] ?? null}
          options={entityOptions}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          onChange={(option) => onChangeEntities(option ? [option] : [])}
        />
      )}
      {onChangeClient ? (
        <Toggle
          size="large"
          alignment="left"
          isDisabled={isDisabled}
          isSelected={client}
          onChange={onChangeClient}
          label={
            <Trans id="settings.organization.entities.agreements.detail.edit.clients">
              Clients
            </Trans>
          }
        />
      ) : null}
    </Stack>
  );
};

const AgreementAppliesWhenEdit = () => {
  const { draft, updateDraft, isUpdateAgreementPending } = useContext(AgreementEditContext);

  const { data: { entities: workshops = [] } = {}, isLoading: isLoadingWorkshops } = useEntities({
    limit: 500,
    offset: 0,
    traits: ['workshop'],
  });
  const { data: { brands = [] } = {}, isLoading: isLoadingBrands } = useBrands({
    limit: 500,
    offset: 0,
  });

  const { t } = useLingui();

  return (
    <Stack gap="1rem" padding="1rem">
      <Stack gap="0.5rem">
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconCloth style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            <Trans id="settings.organization.entities.agreements.detail.applies-when.brands">
              This agreement applies to the following brands:
            </Trans>
          </Paragraph>
        </Stack>
        <InputSearchSelect
          isMulti
          size="medium"
          isLoading={isLoadingBrands}
          isDisabled={isUpdateAgreementPending}
          aria-label={t({
            id: 'settings.organization.entities.agreements.detail.edit.brands',
            message: 'Brands',
          })}
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.edit.brands.placeholder',
            message: 'Select brands',
          })}
          value={draft.articleBrands}
          options={brands}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          onChange={(options) => updateDraft({ articleBrands: [...options] })}
          onRemoveValue={(id) => {
            updateDraft({
              articleBrands: draft.articleBrands.filter((brand) => brand.id !== id),
            });
          }}
        />
      </Stack>

      <Stack gap="0.5rem">
        <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
          <IconWorkshop style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
          <Paragraph>
            <Trans id="settings.organization.entities.agreements.detail.applies-when.workshops">
              This agreement applies when the assigned workshop is one of the following:
            </Trans>
          </Paragraph>
        </Stack>
        <InputSearchSelect
          isMulti
          size="medium"
          isLoading={isLoadingWorkshops}
          isDisabled={isUpdateAgreementPending}
          aria-label={t({
            id: 'settings.organization.entities.agreements.detail.edit.workshops',
            message: 'Workshops',
          })}
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.edit.workshops.placeholder',
            message: 'Select workshops',
          })}
          value={draft.articleWorkshops}
          options={workshops}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          onChange={(options) => updateDraft({ articleWorkshops: [...options] })}
          onRemoveValue={(id) => {
            updateDraft({
              articleWorkshops: draft.articleWorkshops.filter((workshop) => workshop.id !== id),
            });
          }}
        />
      </Stack>
    </Stack>
  );
};

const PriceCatalogAgreementEdit = () => {
  const { draft, updateDraft, isUpdateAgreementPending } = useContext(AgreementEditContext);

  const { t, i18n } = useLingui();

  const amountToConsiderOptions = Object.entries(AMOUNT_TO_CONSIDER).map(([id, label]) => ({
    id: id as AgreementFromGetById['amountToConsider'],
    text: i18n._(label),
  }));

  const currencyOptions = CURRENCIES.map((currency) => ({
    id: currency,
    text: currency,
  }));

  return (
    <>
      <Box>
        <Stack row gap="1rem">
          <Stack gap="0.5rem">
            <InlineText weight="medium">
              <Trans id="settings.organization.entities.agreements.detail.default-custom-actions-currency.label">
                Currency
              </Trans>
            </InlineText>
            <InputSelect
              size="medium"
              isDisabled={isUpdateAgreementPending}
              aria-label={t({
                id: 'settings.organization.entities.agreements.detail.edit.currency',
                message: 'Currency',
              })}
              placeholder={t({
                id: 'settings.organization.entities.agreements.detail.edit.currency.placeholder',
                message: 'Select currency',
              })}
              options={currencyOptions}
              value={currencyOptions.find((option) => option.id === draft.currency)}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.text}
              onChange={(option) => updateDraft({ currency: option?.id ?? null })}
              style={{ width: '10rem' }}
            />
          </Stack>
          {draft.clientB && (
            <Stack gap="0.5rem">
              <InlineText weight="medium">
                <Trans id="settings.organization.entities.agreements.detail.default-margin.label">
                  Default margin
                </Trans>
              </InlineText>
              <InputPercentage
                size="small"
                isDisabled={isUpdateAgreementPending}
                ariaLabel={t({
                  id: 'settings.organization.entities.agreements.detail.edit.margin',
                  message: 'Default margin',
                })}
                value={draft.margin ?? NaN}
                onChange={(value) => updateDraft({ margin: Number.isNaN(value) ? null : value })}
                style={{ width: '10rem' }}
              />
            </Stack>
          )}

          <Stack gap="0.5rem">
            <InlineText weight="medium">
              <Trans id="settings.organization.entities.agreements.detail.default-custom-actions-tax-rates.label">
                Default custom actions tax rates
              </Trans>
            </InlineText>
            <InputPercentage
              size="small"
              isDisabled={isUpdateAgreementPending}
              ariaLabel={t({
                id: 'settings.organization.entities.agreements.detail.edit.tax-rate',
                message: 'Tax rate',
              })}
              value={draft.defaultCustomPriceTaxRates[0]}
              onChange={(value) => {
                updateDraft({
                  defaultCustomPriceTaxRates: Number.isNaN(value) ? [] : [value],
                });
              }}
              style={{ width: '10rem' }}
            />
          </Stack>

          <Stack gap="0.5rem">
            <InlineText weight="medium">
              <Trans id="settings.organization.entities.agreements.detail.amount-to-consider.label">
                Price defined from
              </Trans>
            </InlineText>
            <InputSelect
              size="medium"
              isDisabled={isUpdateAgreementPending}
              aria-label={t({
                id: 'settings.organization.entities.agreements.detail.amount-to-consider.label',
                message: 'Price defined from',
              })}
              options={amountToConsiderOptions}
              value={amountToConsiderOptions.find((option) => option.id === draft.amountToConsider)}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.text}
              onChange={(option) => {
                if (option) {
                  updateDraft({ amountToConsider: option.id });
                }
              }}
              style={{ width: '10rem' }}
            />
          </Stack>
        </Stack>
      </Box>
      <PriceCatalogAgreementTables agreement={draft} />
    </>
  );
};

const TransitAgreementEdit = () => {
  const { draft, updateDraft, isUpdateAgreementPending } = useContext(AgreementEditContext);

  return (
    <Box>
      <Stack>
        <Toggle
          size="small"
          alignment="left"
          isDisabled={isUpdateAgreementPending}
          isSelected={draft.selfHandledTransitEnabled}
          onChange={(selfHandledTransitEnabled) => updateDraft({ selfHandledTransitEnabled })}
          label={
            <Trans id="settings.organization.entities.agreements.detail.self-handled-transit-enabled.label">
              Self-handled transit enabled
            </Trans>
          }
        />
      </Stack>
    </Box>
  );
};

const AfterSalesAgreementEdit = () => {
  const { t } = useLingui();
  const { draft, updateDraft, isUpdateAgreementPending } = useContext(AgreementEditContext);
  const countries = useCountries();

  const selectedCountries = draft.supportedCountries
    .map((code) => countries.find((country) => country.code === code))
    .filter((country) => !!country);

  return (
    <Box>
      <Stack gap="0.5rem">
        <InlineText weight="medium">
          <Trans id="settings.organization.entities.agreements.detail.supported-countries.label">
            Client countries
          </Trans>
        </InlineText>
        <InputSearchSelect
          isMulti
          size="medium"
          isDisabled={isUpdateAgreementPending}
          aria-label={t({
            id: 'settings.organization.entities.agreements.detail.edit.supported-countries',
            message: 'Client countries',
          })}
          placeholder={t({
            id: 'settings.organization.entities.agreements.detail.edit.supported-countries.placeholder',
            message: 'Select countries',
          })}
          value={selectedCountries}
          options={countries}
          getOptionValue={(option) => option.code}
          getOptionLabel={(option) => option.name}
          onChange={(options) =>
            updateDraft({ supportedCountries: options.map((option) => option.code) })
          }
          onRemoveValue={(code) => {
            updateDraft({
              supportedCountries: draft.supportedCountries.filter((country) => country !== code),
            });
          }}
        />
      </Stack>
    </Box>
  );
};

const AgreementDelete = () => {
  const { draft, isUpdateAgreementPending } = useContext(AgreementEditContext);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutateAsync: deleteAgreement, isPending: isDeleteAgreementPending } = useDeleteAgreement(
    draft.id
  );
  const navigate = useNavigate();
  const showToast = useShowToast();

  const isBusy = isUpdateAgreementPending || isDeleteAgreementPending;

  return (
    <>
      <Box>
        <Stack gap="1rem">
          <Title>
            <Trans id="settings.organization.entities.agreements.detail.delete.title">
              Delete this agreement
            </Trans>
          </Title>
          <AgreementDeleteDescription type={draft.type} />
          <Stack row gap="0.5rem" justifyContent="flex-end">
            <div>
              <Button
                variant="secondary-danger"
                size="medium"
                onPress={() => setShowDeleteConfirmation(true)}
                isLoading={isDeleteAgreementPending}
                disabled={isBusy}
              >
                <Trans id="settings.organization.entities.agreements.detail.delete.button">
                  Delete agreement
                </Trans>
              </Button>
            </div>
          </Stack>
        </Stack>
      </Box>
      <ConfirmationDialog
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        title={
          <Trans id="settings.organization.entities.agreements.detail.delete.confirmation.title">
            Are you sure you want to delete this agreement?
          </Trans>
        }
        confirm={
          <Trans id="settings.organization.entities.agreements.detail.delete.confirmation.confirm">
            Delete
          </Trans>
        }
        onConfirm={async () => {
          try {
            await deleteAgreement();
            navigate('/settings_beta/network/agreements');
          } catch (error) {
            showToast({
              text: (
                <Trans id="settings.organization.entities.agreements.detail.delete.confirmation.error">
                  Could not delete the agreement. Please try again.
                </Trans>
              ),
              type: 'error',
            });

            throw error;
          }
        }}
        maxWidth="35rem"
        isPending={isDeleteAgreementPending}
      >
        <Stack gap="0.5rem">
          <AgreementDeleteDescription type={draft.type} />
          <Paragraph weight="heavy">
            <Trans id="settings.organization.entities.agreements.detail.delete.confirmation.cannot-be-undone">
              This action cannot be undone.
            </Trans>
          </Paragraph>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};

const AgreementDeleteDescription = ({ type }: { type: AgreementFromGetById['type'] }) => {
  if (type === 'price_catalog') {
    return (
      <Paragraph>
        <Trans id="settings.organization.entities.agreements.detail.delete.description.price-catalog">
          Deleting this agreement will remove the ability to add prices for actions/packs/spare
          parts/fees between those entities. Existing prices on existing requests will remain in
          place.
        </Trans>
      </Paragraph>
    );
  }

  if (type === 'transit') {
    return (
      <Paragraph>
        <Trans id="settings.organization.entities.agreements.detail.delete.description.transit">
          Deleting this agreement will remove the ability to create new self-handled shipments
          between those entities. Existing self-handled shipments will continue to work.
        </Trans>
      </Paragraph>
    );
  }

  if (type === 'after_sales') {
    return (
      <Paragraph>
        <Trans id="settings.organization.entities.agreements.detail.delete.description.after-sales">
          Deleting this agreement will stop routing new requests to this after-sales entity based on
          the configured stores, groups or client countries. Existing requests keep their current
          after-sales assignment.
        </Trans>
      </Paragraph>
    );
  }

  return null;
};
